import { useCallback, useRef, useState } from "react";

/**
 * Shared "buffered dialog form" primitive.
 *
 * A buffered dialog form holds a working DRAFT copy of some dialog state that
 * the user edits locally; edits are reconciled to the server only on an
 * explicit Save, and Cancel/Close discards the draft. This is the counter-model
 * to save-on-blur / save-on-every-click persistence.
 *
 * The hook is deliberately domain-agnostic: it owns the baseline↔draft sync and
 * the dirty comparison; the consumer owns how a draft is diffed into
 * persistence calls on save. Pair it with {@link BufferedDialogButtons} for the
 * canonical Save/Cancel footer.
 *
 * Usage:
 * ```ts
 * const form = useBufferedDialogForm<Row[]>(rowsEqual);
 * // after the initial load and after a successful Save:
 * form.load(freshRows);
 * // on edit:
 * form.setDraft((rows) => [...(rows ?? []), newRow]);
 * // Save is enabled while form.isDirty; Cancel calls form.reset().
 * ```
 *
 * @param isEqual compares a draft against the last-loaded baseline to decide
 *   `isDirty`. Defaults to `Object.is` (reference equality); pass a structural
 *   comparator for object/array drafts.
 */
export function useBufferedDialogForm<T>(
    isEqual: (a: T, b: T) => boolean = Object.is,
) {
    // Kept in a ref so `reset` has a stable identity yet always reads the latest
    // baseline (the state copy below only exists to drive re-renders/isDirty).
    const baselineRef = useRef<T | null>(null);
    const [baseline, setBaseline] = useState<T | null>(null);
    const [draft, setDraft] = useState<T | null>(null);

    // Adopt a freshly-loaded server value as the new baseline AND draft (in
    // sync). Call after the initial load and after a successful Save.
    const load = useCallback((value: T) => {
        baselineRef.current = value;
        setBaseline(value);
        setDraft(value);
    }, []);

    // Discard local edits: draft ← baseline.
    const reset = useCallback(() => {
        setDraft(baselineRef.current);
    }, []);

    const isDirty =
        baseline !== null && draft !== null && !isEqual(baseline, draft);

    return {
        baseline,
        draft,
        setDraft,
        load,
        reset,
        isDirty,
        isLoaded: baseline !== null,
    };
}
